const ACTIVE_WITHDRAWAL_STATUSES = ['pending', 'approved', 'processing'];

function hasFiniteValue(value) {
  return Number.isFinite(Number(value));
}

function hasExplicitTotals(item) {
  return Boolean(item?.totals) && (
    hasFiniteValue(item.totals.receivable_now)
    || hasFiniteValue(item.totals.waiting_release)
    || hasFiniteValue(item.totals.in_transfer)
    || hasFiniteValue(item.totals.failed_recovered)
    || hasFiniteValue(item.totals.received)
  );
}

export function toFiniteNumber(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function calculateFinanceHeaderBuckets({
  eventBalanceSummary = [],
  ledgerEntries = [],
  summaryData = null,
  preferSummaryTotals = false,
} = {}) {
  const grossCollectedFromLedger = ledgerEntries
    .filter((entry) => entry.entry_type === 'credit')
    .reduce((sum, entry) => sum + toFiniteNumber(entry.gross_amount), 0);

  const explicitBuckets = eventBalanceSummary.reduce((acc, item) => {
    if (!hasExplicitTotals(item)) return acc;

    acc.receivable += toFiniteNumber(item.totals.receivable_now);
    acc.waitingRelease += toFiniteNumber(item.totals.waiting_release);
    acc.inTransfer += toFiniteNumber(item.totals.in_transfer);
    acc.failedTransfer += toFiniteNumber(item.totals.failed_recovered);
    acc.received += toFiniteNumber(item.totals.received);
    return acc;
  }, {
    receivable: 0,
    waitingRelease: 0,
    inTransfer: 0,
    failedTransfer: 0,
    received: 0,
  });

  const legacyItems = eventBalanceSummary.filter((item) => !hasExplicitTotals(item));

  const legacyReceivable = legacyItems.reduce((sum, item) => sum
    + toFiniteNumber(item.available_amount_pix)
    + toFiniteNumber(item.available_amount_card), 0);

  const legacyWaitingRelease = legacyItems.reduce((sum, item) => sum
    + toFiniteNumber(item.pending_amount_pix)
    + toFiniteNumber(item.pending_amount_card), 0);

  const legacyInTransferFromRequests = legacyItems
    .filter((item) => ACTIVE_WITHDRAWAL_STATUSES.includes(item.withdrawal_request_status))
    .reduce((sum, item) => sum + toFiniteNumber(item.withdrawal_request_amount), 0);

  const inTransferFromItems = explicitBuckets.inTransfer + legacyInTransferFromRequests;

  const inTransferFromLedger = ledgerEntries
    .filter((entry) => entry.entry_type === 'credit'
      && entry.withdrawal_id
      && ACTIVE_WITHDRAWAL_STATUSES.includes(entry.withdrawal_status))
    .reduce((sum, entry) => sum + toFiniteNumber(entry.net_amount), 0);

  const inTransfer = Math.max(inTransferFromItems, inTransferFromLedger);
  const extraInTransferFromLedger = Math.max(inTransfer - inTransferFromItems, 0);

  const legacyFailedTransfer = legacyItems.reduce(
    (sum, item) => sum + toFiniteNumber(item.recovery_amount_pix),
    0,
  );

  const receivedFromLedger = ledgerEntries
    .filter((entry) => entry.entry_type === 'credit'
      && entry.withdrawal_id
      && entry.withdrawal_status === 'completed')
    .reduce((sum, entry) => sum + toFiniteNumber(entry.net_amount), 0);

  return {
    grossCollected: preferSummaryTotals
      ? toFiniteNumber(summaryData?.totalReceived, grossCollectedFromLedger)
      : grossCollectedFromLedger,
    receivable: Math.max(
      explicitBuckets.receivable
        + legacyReceivable
        - legacyInTransferFromRequests
        - extraInTransferFromLedger,
      0,
    ),
    waitingRelease: explicitBuckets.waitingRelease + legacyWaitingRelease,
    inTransfer,
    failedTransfer: explicitBuckets.failedTransfer + legacyFailedTransfer,
    received: Math.max(explicitBuckets.received, receivedFromLedger),
  };
}
